import * as React from 'react'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'

import Grid from '@mui/material/Grid'

import Container from '@mui/material/Container'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import LoadingButton from '@mui/lab/LoadingButton'

import { renderImportOptionsMenu } from './SubImportMenuItem'

import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import StepButton from '@mui/material/StepButton'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import ListItem from '@mui/material/ListItem'

import useMediaQuery from '@mui/material/useMediaQuery'

import Typography from '@mui/material/Typography'

import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined'

import EditableSelect from '../EditableSelect'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'

import {
    useContextsStore,
    selectOwnedContexts,
} from '../../stores/useContextsStore'
import { UserContext } from '../../UserContext'

import { useTheme } from '@mui/material/styles'

import { SubImportContext } from './SubImportContext'
import { DialogsContext, DialogsProvider } from '../../DialogsContext'
import { useTrackSearch } from '../../hooks/useTrackSearch'

import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import FormControlLabel from '@mui/material/FormControlLabel'

import Switch from '@mui/material/Switch'

import axios from 'axios'
import qs from 'qs'

import { generateTextImportItems } from './TextImportItems'
import { genesisText } from './samples/sampleTexts'

import {
    slugifyString,
    convertGPTResponsesToArray,
    isValidUrl,
    getUrlTypeAndCode,
} from '../../utils/index'
import ImportingNotice from './ImportingNotice'
import { chipHoverAccent } from '../../theme'
import { importErrorMessage } from './importError'

const steps = ['Start', 'Sources', 'Settings', 'Results']

const textCategories = [
    { key: 'popular', label: 'Popular' },
    { key: 'web', label: 'Search the Web' },
    { key: 'ai', label: 'AI Generated' },
    { key: 'social', label: 'Social Media' },
    { key: 'products', label: 'Products' },
    { key: 'text', label: 'Add Plain Text' },
    { key: 'files', label: 'Import Files' },
    { key: 'mindmapping', label: 'Mind Maps' },
    { key: 'kgs', label: 'Knowledge Graphs & Ontologies' },
    { key: 'allShortText', label: 'All' },
]

// When the user returns to this generic picker from an import, auto-select the
// chip that import belongs to (e.g. YouTube → Social, Google → Web, Amazon →
// Products). The chip is derived from the import's own type: each menu item
// declares which chips it lives under via its `textInput` tags, so we look up
// the item matching the dialog we came from and take its highest-priority chip.
// When an import belongs to several chips, this order picks the most specific
// one ('web'/'popular' are last because they're the most generic).
const CHIP_PRIORITY = [
    'social',
    'products',
    'ai',
    'mindmapping',
    'kgs',
    'files',
    'text',
    'web',
    'popular',
]

const chipForDialog = (dialogType, items) => {
    if (!dialogType) return 'popular'
    const tags = new Set(
        items
            .filter(item => item.type === dialogType)
            .flatMap(item => item.textInput || [])
    )
    return CHIP_PRIORITY.find(chip => tags.has(chip)) || 'popular'
}

export default function ShortTextImportDialog({
    presetContext = '',

    open,
    onClose,
}) {
    const theme = useTheme() // hook to get the current theme

    const {
        activeSubImportDialog,
        setActiveSubImportDialog,
    } = React.useContext(SubImportContext)

    const {
        activeImportDialog,
        setActiveImportDialog,
        previousImportDialog,
        setBackLinkDialog,
        presetSearchString,
        setPresetSearchString,
        openImportDialog,
        closeImportDialog,
    } = React.useContext(DialogsContext)

    // Shared across all import sources so the typed query persists when switching
    const searchString = presetSearchString
    const setSearchString = setPresetSearchString

    const isMobile = useMediaQuery('(max-width:600px)')

    const [selectedContext, setSelectedContext] = React.useState(presetContext)

    const [errorMessage, setErrorMessage] = React.useState(null)

    const [importError, setImportError] = React.useState(null)

    const [completedSteps, setCompletedSteps] = React.useState([])

    const [visitedContext, setVisitedContext] = React.useState(false)

    const [numberOfResults, setNumberOfResults] = React.useState('10')

    const [modelToUse, setModelToUse] = React.useState('gpt4')

    const [hideSearchTerms, setHideSearchTerms] = React.useState(true)

    const [advancedSettings, setAdvancedSettings] = React.useState(false)

    const [fromInsideContext, setFromInsideContext] = React.useState(false)

    const { username } = React.useContext(UserContext)

    React.useState(() => {
        setActiveImportDialog('shortTextImport')
        setBackLinkDialog('backLink')
    }, [])

    const menuItems = generateTextImportItems()

    // Derive which chip to open on from the import the user came from (its type
    // is held in previousImportDialog.current; the ref still holds the prior
    // value during this first render — see DialogsContext).
    const initialChip = chipForDialog(previousImportDialog?.current, menuItems)

    const [selectedTextInputs, setSelectedTextInputs] = React.useState([
        initialChip,
    ])

    const [selectedChip, setSelectedChip] = React.useState(initialChip)

    const [searchQuery, setSearchQuery] = React.useState('')

    const trackSearch = useTrackSearch('short_text_import', username)

    const handleSearchChange = e => {
        const value = e.target.value
        setSearchQuery(value)
        // A search query looks across everything, so the chip filter no longer
        // applies — reflect that by switching to the "All" chip while typing.
        if (value && selectedChip !== 'allShortText') {
            setSelectedTextInputs(['allShortText'])
            setSelectedChip('allShortText')
        }
        trackSearch(value)
    }

    const chipFilteredMenuItems =
        selectedTextInputs == []
            ? menuItems
            : menuItems.filter(({ textInput }) =>
                  selectedTextInputs.some(selectedTextInput =>
                      textInput.includes(selectedTextInput)
                  )
              )

    const filteredMenuItems = searchQuery
        ? chipFilteredMenuItems.filter(item => {
              const q = searchQuery.toLowerCase()
              return (
                  (item.name && item.name.toLowerCase().includes(q)) ||
                  (item.description &&
                      item.description.toLowerCase().includes(q))
              )
          })
        : chipFilteredMenuItems

    const contexts = useContextsStore(selectOwnedContexts)
    const contextNames = contexts.map(c => c.name)

    const [activeStep, setActiveStep] = React.useState(1)
    const [skipped, setSkipped] = React.useState(new Set())

    const isStepOptional = step => {
        return step === 1
    }

    const isStepSkipped = step => {
        return skipped.has(step)
    }

    const handleNext = () => {
        let newSkipped = skipped
        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values())
            newSkipped.delete(activeStep)
        }

        setActiveStep(prevActiveStep => prevActiveStep + 1)
        setSkipped(newSkipped)
    }

    React.useEffect(() => {
        if (activeSubImportDialog.type) {
            setActiveStep(1)
            setActiveImportDialog(activeSubImportDialog.type)
        }
    }, [activeSubImportDialog])

    const submitDataForm = () => {
        const redirectUrl = fromInsideContext
            ? ``
            : `/${username}/${selectedContext}/edit`

        const dataToSend = {
            context: selectedContext,
            addedContexts: selectedContext,
            mode: modelToUse === 'gpt4' ? 'gptchat' : 'continue',
            entry: { body: searchString },
            addedCategories: `[ideas]`,
            redirectUrl: redirectUrl,
        }

        const dataAsForm = qs.stringify(dataToSend)

        axios
            .post(`/post`, dataAsForm, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded', // Sending as URL-encoded form data
                },
            })
            .then(response => {
                if (response.data.redirectUrl) {
                    // If you're using window.location
                    window.location.href = response.data.redirectUrl

                    // Or, if you're using React Router's history (assuming you have access to the history object)
                    // history.push(response.data.redirectUrl);
                } else {
                    console.error(`Did't receive any response from the server.`)
                    if (response.data.error) {
                        setErrorMessage(
                            typeof response.data.error == 'object'
                                ? response.data.error.error ||
                                      JSON.stringify(response.data.error)
                                : response.data.error
                        )
                        setImportError(response.data.error)
                        setActiveStep(1)
                    }
                }
            })
            .catch(error => {
                // Handle the error here
                console.error('Received the following error', error)
                setErrorMessage(importErrorMessage(error))
                setImportError(response.data.error)
            })
    }

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1)
    }

    const handleSearchStringChange = event => {
        setSearchString(event.target.value)
        setPresetSearchString(event.target.value)
    }

    React.useEffect(() => {
        switch (activeStep) {
            case 0:
                setActiveSubImportDialog(false)
                setActiveImportDialog('import')
                break
            case 1:
                setActiveSubImportDialog(false)
                if (!presetContext) {
                    setSelectedContext(generatePresetContext())
                }

                break
            case 2:
                if (!searchString) {
                    setErrorMessage(
                        `Please, provide a search query you'd like to get results from AI for.`
                    )
                    setActiveStep(1)
                    break
                }
                if (!selectedContext) {
                    setErrorMessage(
                        'Please, select the graph to import your data to.'
                    )
                    setActiveStep(1)
                    break
                }
                if (selectedContext) {
                    setErrorMessage(null)
                    submitDataForm()
                    break
                }
                setActiveStep(1)
                break
        }
    }, [activeStep])

    const handleStep = step => {
        setActiveStep(step)
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth={true}
                maxWidth="lg"
            >
                <DialogTitle
                    sx={{
                        padding: 4,
                        color: theme => theme.palette.text.primary,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {'Analyze a text or a discourse related to a topic'}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Stepper
                    activeStep={activeStep}
                    sx={{ paddingLeft: 4, paddingRight: 4, paddingTop: 2 }}
                >
                    {steps.map((label, index) => {
                        const stepProps = {}
                        const labelProps = {}
                        // if (isStepOptional(index)) {
                        //     labelProps.optional = (
                        //         <Typography variant="caption">
                        //             Optional
                        //         </Typography>
                        //     )
                        // }
                        if (isStepSkipped(index)) {
                            stepProps.completed = false
                        }
                        return (
                            <Step key={label} {...stepProps}>
                                <StepButton
                                    onClick={() => handleStep(index)}
                                    completed={(
                                        completedSteps.indexOf(index) >= 0
                                    ).toString()}
                                >
                                    <StepLabel
                                        {...labelProps}
                                        sx={{
                                            '& .MuiStepLabel-label': {
                                                fontSize: {
                                                    xs: '0.75rem',
                                                    sm: 'initial',
                                                },
                                            },
                                        }}
                                    >
                                        {label}
                                    </StepLabel>
                                </StepButton>
                            </Step>
                        )
                    })}
                </Stepper>

                {errorMessage && (
                    <Typography
                        sx={{
                            paddingRight: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                            paddingBottom: 0,
                        }}
                        color="error"
                        component="div"
                        gutterBottom
                    >
                        {errorMessage}
                    </Typography>
                )}

                {activeStep === 1 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 4,
                            }}
                        >
                            <Grid container spacing={4}>
                                <Grid item xs={12} sm={7}>
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 2 }}
                                    >
                                        <TextField
                                            label={
                                                'Add your text, keywords, or a topic'
                                            }
                                            value={searchString}
                                            variant="outlined"
                                            onChange={handleSearchStringChange}
                                            aria-describedby="searchString-helperText"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderWidth: 2,
                                                        borderColor:
                                                            'info.main',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderWidth: 2,
                                                        borderColor:
                                                            'info.dark',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderWidth: 2,
                                                    },
                                                },
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                mt: 2.5,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontWeight: 500 }}
                                            >
                                                try one of these:
                                            </Typography>
                                            {[
                                                {
                                                    label: 'ai agents',
                                                    value: 'ai agents',
                                                },
                                                {
                                                    label: 'nvidia',
                                                    value: 'nvidia',
                                                },
                                                {
                                                    label: 'nanomaterials',
                                                    value: 'nanomaterials',
                                                },
                                                {
                                                    label: 'Bible, Genesis',
                                                    value: genesisText,
                                                    importType: 'textEditor',
                                                },
                                            ].map(suggestion => (
                                                <Chip
                                                    key={suggestion.label}
                                                    label={suggestion.label}
                                                    size="medium"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        setSearchString(
                                                            suggestion.value
                                                        )
                                                        setPresetSearchString(
                                                            suggestion.value
                                                        )
                                                        if (
                                                            suggestion.importType
                                                        ) {
                                                            setActiveSubImportDialog(
                                                                {
                                                                    type:
                                                                        suggestion.importType,
                                                                }
                                                            )
                                                        }
                                                    }}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        fontSize: '0.85rem',
                                                        fontWeight: 500,
                                                        borderColor:
                                                            'rgba(0, 200, 200, 0.5)',
                                                        color: 'info.main',
                                                        '&:hover': {
                                                            color: chipHoverAccent,
                                                            borderColor: chipHoverAccent,
                                                        },
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    {' '}
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginBottom: 2,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                paddingRight: 1,
                                                paddingLeft: 2,
                                                paddingTop: 1,
                                                paddingBottom: 0,
                                            }}
                                            color="text.primary"
                                            component="div"
                                            gutterBottom
                                        >
                                            choose a content import app:
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                alignItems: 'center',
                                                p: 0,
                                                listStyle: 'none',
                                                m: 0,
                                                '& > *': {
                                                    m: '4px',
                                                },
                                            }}
                                        >
                                            {textCategories.map(category => {
                                                const onChipClick =
                                                    selectedChip ===
                                                    category.key
                                                        ? null
                                                        : () => {
                                                              setSelectedTextInputs(
                                                                  [category.key]
                                                              )
                                                              setSelectedChip(
                                                                  category.key
                                                              )
                                                          }

                                                return (
                                                    <ListItem
                                                        key={category.key}
                                                        sx={{
                                                            p: 0,
                                                            width: 'auto',
                                                            display:
                                                                'inline-flex',
                                                        }}
                                                    >
                                                        <Chip
                                                            label={
                                                                category.label
                                                            }
                                                            color={
                                                                selectedChip ===
                                                                category.key
                                                                    ? 'primary'
                                                                    : 'default'
                                                            }
                                                            sx={{
                                                                m: 0,
                                                                fontSize: isMobile
                                                                    ? '0.8rem'
                                                                    : '1rem',
                                                                fontWeight:
                                                                    '600',
                                                                height: isMobile
                                                                    ? theme =>
                                                                          theme.spacing(
                                                                              3
                                                                          )
                                                                    : theme =>
                                                                          theme.spacing(
                                                                              4.5
                                                                          ),
                                                                paddingLeft: isMobile
                                                                    ? theme =>
                                                                          theme.spacing(
                                                                              0.5
                                                                          )
                                                                    : theme =>
                                                                          theme.spacing(
                                                                              1.5
                                                                          ),
                                                                paddingRight: isMobile
                                                                    ? theme =>
                                                                          theme.spacing(
                                                                              0.5
                                                                          )
                                                                    : theme =>
                                                                          theme.spacing(
                                                                              1.5
                                                                          ),
                                                            }}
                                                            onClick={
                                                                onChipClick
                                                            }
                                                        />
                                                    </ListItem>
                                                )
                                            })}
                                            <TextField
                                                value={searchQuery}
                                                onChange={handleSearchChange}
                                                placeholder="Search apps…"
                                                variant="outlined"
                                                size="small"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{
                                                    width: 200,
                                                    maxWidth: '100%',
                                                    ml: 1,
                                                    '& .MuiOutlinedInput-root': {
                                                        height: theme =>
                                                            theme.spacing(
                                                                isMobile
                                                                    ? 3
                                                                    : 4.5
                                                            ),
                                                        fontSize: isMobile
                                                            ? '0.8rem'
                                                            : '1rem',
                                                        borderRadius: theme =>
                                                            theme.spacing(
                                                                isMobile
                                                                    ? 3
                                                                    : 4.5
                                                            ),
                                                    },
                                                }}
                                            />
                                        </Box>

                                        <DialogContent
                                            sx={{
                                                pl: 0,
                                                ml: 0,
                                                marginTop: 1,
                                            }}
                                        >
                                            {renderImportOptionsMenu(
                                                filteredMenuItems
                                            )}
                                        </DialogContent>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </>
                )}
                {activeStep === 2 && (
                    <ImportingNotice
                        linkHref={`/${username}/${selectedContext}/edit`}
                    />
                )}
                <DialogActions
                    sx={{
                        paddingRight: 4,
                        paddingLeft: 4,
                        paddingBottom: 4,
                        paddingTop: 2,
                    }}
                >
                    {/* <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button> */}
                    <Button
                        color="inherit"
                        variant="outlined"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1, px: 3, py: 1, fontSize: '0.95rem' }}
                    >
                        Back
                    </Button>
                    {/* {isStepOptional(activeStep) && (
                        <Button
                            color="inherit"
                            onClick={handleSkip}
                            sx={{ mr: 1 }}
                        >
                            Skip
                        </Button>
                    )} */}
                    <LoadingButton
                        color="info"
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        disabled={!Boolean(activeSubImportDialog.type)}
                        onClick={handleNext}
                        sx={{ px: 4, py: 1, fontSize: '0.95rem' }}
                    >
                        {activeStep === steps.length - 1 ? 'Visualize' : 'Next'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}

function generatePresetContext(searchString) {
    const dateAndTime = new Date()
        .toISOString()
        .slice(2, 16)
        .replace(/-/g, '')
        .replace(/:/g, '')

    if (!searchString) return `idea_${dateAndTime}`

    const twoFirstSearchQueryWords = slugifyString(
        searchString
            .split(' ')
            .slice(0, 2)
            .join('_')
            .slice(0, 20)
            .replace(/[^\P{L}a-z][^a-z]*/giu, '')
    )

    return `idea_${twoFirstSearchQueryWords}`
}
